import { useCurrency } from "../hooks/useCurrency";
import { Seo } from "../components/Seo";
import { useState, useEffect } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Plane, Check, Minus, Plus, Zap, Code2, Sparkles, ArrowLeft, LayoutDashboard, Sun, Moon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const DISCOUNT = 0.25;
const money = (n) => (n === 0 ? "0" : n.toFixed(2));

const THEMES = {
  dark: {
    page: "bg-[#05050A] text-white",
    nav: "bg-[#05050A]/80 backdrop-blur-xl border-b border-white/5",
    navLink: "text-slate-300 hover:text-white",
    brand: "text-white",
    heroGlow: "bg-[radial-gradient(ellipse_at_top,_rgba(0,122,255,0.18),_#05050A_60%)]",
    pill: "border-white/10 bg-white/5 text-[#00F0FF]",
    overline: "text-[#00F0FF]",
    heading: "text-white",
    gradient: "from-[#00F0FF] to-[#007AFF]",
    sub: "text-slate-300",
    muted: "text-slate-400",
    muted2: "text-slate-500",
    toggleWrap: "border-white/10 bg-[#0D0E15]",
    toggleInactive: "text-slate-300 hover:text-white",
    saveBadge: "bg-[#00F0FF]/15 text-[#00F0FF]",
    card: "bg-[#0D0E15] border border-white/5",
    proCard: "bg-gradient-to-b from-[#0D0E15] to-[#151722] border border-[#007AFF] shadow-[0_0_40px_rgba(0,122,255,0.2)]",
    priceText: "text-white",
    featureLi: "text-slate-300",
    check: "text-[#00F0FF]",
    sideCta: "bg-white/5 hover:bg-white/10 text-white border border-white/10",
    innerBox: "border-white/10 bg-[#05050A]/60",
    stepper: "border-white/15 text-white hover:bg-white/10",
    tierActive: "border-[#00F0FF] bg-[#00F0FF]/10",
    tierIdle: "border-white/10 bg-[#05050A]/60 hover:border-white/25",
    tierText: "text-slate-200",
    themeBtn: "border-white/15 text-slate-200 hover:bg-white/5 hover:text-white",
    footerBorder: "border-white/5",
    footerText: "text-slate-400 hover:text-white",
  },
  light: {
    page: "bg-[#F6F8FC] text-slate-900",
    nav: "bg-white/80 backdrop-blur-xl border-b border-slate-200",
    navLink: "text-slate-600 hover:text-slate-900",
    brand: "text-slate-900",
    heroGlow: "bg-[radial-gradient(ellipse_at_top,_rgba(0,122,255,0.14),_#F6F8FC_60%)]",
    pill: "border-slate-200 bg-white text-[#0284C7] shadow-sm",
    overline: "text-[#0284C7]",
    heading: "text-slate-900",
    gradient: "from-[#007AFF] to-[#00B8D4]",
    sub: "text-slate-600",
    muted: "text-slate-500",
    muted2: "text-slate-400",
    toggleWrap: "border-slate-200 bg-white shadow-sm",
    toggleInactive: "text-slate-500 hover:text-slate-900",
    saveBadge: "bg-[#007AFF]/10 text-[#0284C7]",
    card: "bg-white border border-slate-200",
    proCard: "bg-white border-2 border-[#007AFF] shadow-[0_16px_48px_rgba(2,132,199,0.18)]",
    priceText: "text-slate-900",
    featureLi: "text-slate-600",
    check: "text-[#007AFF]",
    sideCta: "bg-slate-100 hover:bg-slate-200 text-slate-900 border border-slate-200",
    innerBox: "border-slate-200 bg-slate-50",
    stepper: "border-slate-300 text-slate-700 hover:bg-slate-100",
    tierActive: "border-[#007AFF] bg-[#007AFF]/10",
    tierIdle: "border-slate-200 bg-white hover:border-slate-300",
    tierText: "text-slate-700",
    themeBtn: "border-slate-300 text-slate-700 hover:bg-slate-100",
    footerBorder: "border-slate-200",
    footerText: "text-slate-500 hover:text-slate-900",
  },
};

const BASIC_FEATURES = [
  "Unlimited PNR conversions",
  "3 itinerary layouts",
  "Flight leg summary",
  "Personal and commercial use",
  "Download PDF",
];

const PLUS_FEATURES = [
  "Everything in Basic",
  "10 itinerary ready to use layouts",
  "Full customisation & agency branding",
  "PDF, HTML & WhatsApp export",
  "Password-protected share links",
  "Priority email support",
];

const API_TIERS = [
  { monthly: 24.99, calls: "10,000 API calls / month" },
  { monthly: 49.99, calls: "25,000 API calls / month" },
  { monthly: 99.99, calls: "50,000 API calls / month" },
  { monthly: 199.99, calls: "Unlimited API calls" },
];

const API_FEATURES = [
  "Public REST /api/convert endpoint",
  "API key management",
  "Sabre & Amadeus parsing",
  "Enriched airlines, airports & logos",
];

function PricingNav({ c, dark, onToggle }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${c.nav}`}>
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="pricing-nav-brand">
          <img src="/pnr-decode-wordmark.png" alt="PNR Decode" className="h-11 w-auto object-contain" />
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/" className={`text-sm font-medium px-3 py-1.5 rounded-md transition-colors inline-flex items-center gap-1.5 ${c.navLink}`} data-testid="pricing-nav-home">
            <ArrowLeft className="h-4 w-4" /> Home
          </Link>
          <Link to="/developers" className={`text-sm font-medium px-3 py-1.5 rounded-md transition-colors ${c.navLink}`} data-testid="pricing-nav-developers">API Docs</Link>
          <button
            data-testid="theme-toggle"
            onClick={onToggle}
            aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
            className={`h-9 w-9 inline-flex items-center justify-center rounded-full border transition-colors ${c.themeBtn}`}
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          {user ? (
            <Button size="sm" className="rounded-full bg-[#007AFF] hover:bg-[#00F0FF] text-white hover:text-black font-semibold transition-colors" onClick={() => navigate("/dashboard")} data-testid="pricing-nav-dashboard">
              <LayoutDashboard className="h-4 w-4 mr-1.5" /> Dashboard
            </Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" className={c.navLink} onClick={() => navigate("/login")} data-testid="pricing-nav-login">Login</Button>
              <Button size="sm" className="rounded-full bg-[#007AFF] hover:bg-[#00F0FF] text-white hover:text-black font-semibold transition-colors" onClick={() => navigate("/register")} data-testid="pricing-nav-signup">Sign up</Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function BillingToggle({ annual, setAnnual, c }) {
  return (
    <div className={`inline-flex items-center rounded-full border p-1 ${c.toggleWrap}`} data-testid="billing-toggle">
      <button
        data-testid="billing-toggle-monthly"
        onClick={() => setAnnual(false)}
        className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${!annual ? "bg-[#007AFF] text-white" : c.toggleInactive}`}
      >
        Monthly
      </button>
      <button
        data-testid="billing-toggle-annual"
        onClick={() => setAnnual(true)}
        className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-2 ${annual ? "bg-[#007AFF] text-white" : c.toggleInactive}`}
      >
        Annually
        <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${annual ? "bg-white/20 text-white" : c.saveBadge}`}>Save 25%</span>
      </button>
    </div>
  );
}

function Feature({ children, c }) {
  return (
    <li className={`flex items-start gap-2.5 text-sm ${c.featureLi}`}>
      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${c.check}`} /> {children}
    </li>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dark, setDark] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("pnr_home_theme") : null;
    return saved ? saved === "dark" : false; // default: light, shared with homepage
  });
  useEffect(() => {
    localStorage.setItem("pnr_home_theme", dark ? "dark" : "light");
  }, [dark]);
  const c = dark ? THEMES.dark : THEMES.light;

  const [annual, setAnnual] = useState(false);
  const [users, setUsers] = useState(1);
  const [apiTier, setApiTier] = useState(0);
  const { fmt } = useCurrency();

  const price = (monthly) => (annual ? monthly * (1 - DISCOUNT) : monthly);
  const period = annual ? "billed annually" : "billed monthly";

  // Pricing page is public-only; logged-in users manage plans from the dashboard.
  if (user) return <Navigate to="/dashboard" replace />;

  const plusPerUser = price(9.99);
  const plusTotalMonthly = plusPerUser * users;
  const apiPrice = price(API_TIERS[apiTier].monthly);

  const go = () => navigate(user ? "/dashboard" : "/register");
  const cardBase = "relative rounded-2xl p-8 flex flex-col";

  return (
    <div className={`min-h-screen antialiased overflow-x-hidden transition-colors duration-300 ${c.page}`} data-testid="pricing-page" data-theme={dark ? "dark" : "light"}>
      <Seo path="/pricing" title="Pricing — Basic, Plus & Conversion API plans" description="Simple pricing for PNR Decode. Start free on Basic, add teammates on Plus ($9.99/user/mo), or scale with the Conversion API. Monthly or annual billing, save 25% yearly." />
      <PricingNav c={c} dark={dark} onToggle={() => setDark((v) => !v)} />

      <section className="relative px-6 pt-32 pb-16 text-center overflow-hidden">
        <div className={`absolute inset-0 ${c.heroGlow}`} aria-hidden />
        <div className="relative z-10 max-w-3xl mx-auto">
          <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] ${c.pill}`}>
            <Sparkles className="h-3.5 w-3.5" /> Pricing
          </div>
          <h1 className={`mt-6 font-heading text-5xl sm:text-6xl font-extrabold tracking-tighter leading-none ${c.heading}`}>
            Plans that scale with <span className={`bg-gradient-to-r ${c.gradient} bg-clip-text text-transparent`}>every trip</span>
          </h1>
          <p className={`mt-5 text-lg ${c.sub}`}>Start free forever. Upgrade for branding, exports and API access. Switch to annual and save 25%.</p>
          <div className="mt-8 flex justify-center">
            <BillingToggle annual={annual} setAnnual={setAnnual} c={c} />
          </div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 items-start">

          {/* BASIC */}
          <div className={`${cardBase} ${c.card}`} data-testid="plan-basic">
            <div className={`text-xs font-bold uppercase tracking-[0.2em] ${c.overline}`}>Basic</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className={`font-heading text-5xl font-extrabold tracking-tighter ${c.priceText}`} data-testid="basic-price">{fmt(0)}</span>
              <span className={`text-sm ${c.muted}`}>/month</span>
            </div>
            <p className={`mt-3 text-sm ${c.muted}`}>Perfect for everyone — fast, unlimited PNR conversion at no cost.</p>
            <Button data-testid="basic-cta" onClick={go} className={`mt-6 w-full rounded-full font-semibold transition-[transform,background-color] duration-200 hover:scale-[1.02] ${c.sideCta}`}>
              Get started free
            </Button>
            <ul className="mt-7 space-y-3">
              {BASIC_FEATURES.map((f) => <Feature key={f} c={c}>{f}</Feature>)}
            </ul>
          </div>

          {/* PLUS */}
          <div className={`${cardBase} ${c.proCard} md:scale-105`} data-testid="plan-plus">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#007AFF] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white">Most Popular</span>
            <div className={`text-xs font-bold uppercase tracking-[0.2em] ${c.overline}`}>Plus</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className={`font-heading text-5xl font-extrabold tracking-tighter ${c.priceText}`} data-testid="plus-price">{fmt(plusPerUser)}</span>
              <span className={`text-sm ${c.muted}`}>/user/mo</span>
            </div>
            <p className={`mt-1 text-xs ${c.muted2}`}>{period}{annual && <> · <span className="line-through">{fmt(9.99)}</span> monthly</>}</p>

            <div className={`mt-5 rounded-xl border p-4 ${c.innerBox}`}>
              <div className="flex items-center justify-between">
                <span className={`text-sm ${c.sub}`}>Users</span>
                <div className="flex items-center gap-3">
                  <button data-testid="plus-users-decrement" onClick={() => setUsers((u) => Math.max(1, u - 1))} disabled={users <= 1}
                    className={`h-8 w-8 rounded-full border flex items-center justify-center disabled:opacity-40 transition-colors ${c.stepper}`} aria-label="Decrease users">
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className={`w-8 text-center font-heading font-bold text-lg ${c.priceText}`} data-testid="plus-users-value">{users}</span>
                  <button data-testid="plus-users-increment" onClick={() => setUsers((u) => Math.min(50, u + 1))} disabled={users >= 50}
                    className={`h-8 w-8 rounded-full border flex items-center justify-center disabled:opacity-40 transition-colors ${c.stepper}`} aria-label="Increase users">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <input type="range" min="1" max="50" value={users} onChange={(e) => setUsers(Number(e.target.value))} className="mt-3 w-full accent-[#007AFF]" data-testid="plus-users-range" aria-label="Number of users" />
              <div className={`mt-3 flex items-baseline justify-between border-t pt-3 ${dark ? "border-white/10" : "border-slate-200"}`}>
                <span className={`text-sm ${c.muted}`}>Total</span>
                <div className="text-right">
                  <div className={`font-heading font-bold ${c.priceText}`} data-testid="plus-total">{fmt(plusTotalMonthly)}<span className={`text-xs font-normal ${c.muted}`}>/mo</span></div>
                  {annual && <div className={`text-[11px] ${c.muted2}`} data-testid="plus-total-annual">{fmt(plusTotalMonthly * 12)} billed annually</div>}
                  <div className={`text-[11px] ${c.muted2}`}>for {users} user{users > 1 ? "s" : ""}</div>
                </div>
              </div>
            </div>

            <Button data-testid="plus-cta" onClick={go} className="mt-6 w-full rounded-full font-semibold bg-[#007AFF] hover:bg-[#00F0FF] text-white hover:text-black transition-[transform,background-color,color] duration-200 hover:scale-[1.02]">
              <Zap className="h-4 w-4 mr-2" /> Start Plus trial
            </Button>
            <ul className="mt-7 space-y-3">
              {PLUS_FEATURES.map((f) => <Feature key={f} c={c}>{f}</Feature>)}
            </ul>
          </div>

          {/* CONVERSION API */}
          <div className={`${cardBase} ${c.card}`} data-testid="plan-api">
            <div className={`text-xs font-bold uppercase tracking-[0.2em] inline-flex items-center gap-2 ${c.overline}`}><Code2 className="h-4 w-4" /> Conversion API</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className={`font-heading text-5xl font-extrabold tracking-tighter ${c.priceText}`} data-testid="api-price">{fmt(apiPrice)}</span>
              <span className={`text-sm ${c.muted}`}>/month</span>
            </div>
            <p className={`mt-1 text-xs ${c.muted2}`}>{period}{annual && <> · <span className="line-through">{fmt(API_TIERS[apiTier].monthly)}</span> monthly</>}</p>
            <p className={`mt-3 text-sm ${c.muted}`}>Integrate PNR conversion into your own product. Pick the volume you need.</p>

            <div className="mt-5 space-y-2">
              {API_TIERS.map((t, i) => (
                <button key={t.monthly} data-testid={`api-tier-${i}`} onClick={() => setApiTier(i)}
                  className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors ${apiTier === i ? c.tierActive : c.tierIdle}`}>
                  <span className={`text-sm ${c.tierText}`}>{t.calls}</span>
                  <span className={`text-sm font-semibold ${c.priceText}`}>{fmt(price(t.monthly))}<span className={`text-xs font-normal ${c.muted}`}>/mo</span></span>
                </button>
              ))}
            </div>
            {annual && <div className={`mt-2 text-[11px] ${c.muted2}`} data-testid="api-annual-note">{fmt(apiPrice * 12)} billed annually</div>}

            <Button data-testid="api-cta" onClick={go} className={`mt-6 w-full rounded-full font-semibold transition-[transform,background-color] duration-200 hover:scale-[1.02] ${c.sideCta}`}>
              Get API access
            </Button>
            <ul className="mt-7 space-y-3">
              {API_FEATURES.map((f) => <Feature key={f} c={c}>{f}</Feature>)}
            </ul>
          </div>
        </div>

        <p className={`mt-12 text-center text-sm ${c.muted2}`}>All prices in USD. Billing is in demo mode — no real charges are made.</p>
      </section>

      <footer className={`border-t px-6 py-10 ${c.footerBorder}`}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <img src="/pnr-decode-wordmark.png" alt="PNR Decode" className="h-9 w-auto object-contain" />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
            <Link to="/" className={`transition-colors ${c.footerText}`}>Back to home</Link>
            <Link to="/privacy-policy" className={`transition-colors ${c.footerText}`}>Privacy</Link>
            <Link to="/terms-of-use" className={`transition-colors ${c.footerText}`}>Terms</Link>
            <Link to="/cookies-policy" className={`transition-colors ${c.footerText}`}>Cookies</Link>
            <Link to="/refund-policy" className={`transition-colors ${c.footerText}`}>Refunds</Link>
            <a href="mailto:support@pnrdecode.com" className={`transition-colors ${c.footerText}`}>Support</a>
          </div>
          <div className={`text-xs ${c.muted2}`}>© {new Date().getFullYear()} PNR Decode. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
